import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import {
  TransitionApi,
  Transition,
  NewTransition,
  UpdateTransition,
  TransitionFilters,
} from '../models';

const transitionsService = createApi({
  reducerPath: 'transitionsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/transitions`),
  tagTypes: ['Transitions'],
  endpoints: (builder) => ({
    getTransitions: builder.query<
      Transition[],
      { filters?: TransitionFilters }
    >({
      query: ({ filters }) => {
        const params: Record<string, string> = {};
        if (filters?.flowId && filters.flowId.trim() !== '') {
          params.flowId = filters.flowId.trim();
        }
        if (
          filters?.logisticOperatorId &&
          filters.logisticOperatorId.trim() !== ''
        ) {
          params.logisticOperatorId = filters.logisticOperatorId.trim();
        }
        return {
          url: '',
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
      transformResponse: (response: TransitionApi[]) =>
        response.map((transition) => ({
          id: transition.id,
          name: transition.name,
          toStatusId: transition.toStatusId,
          flowId: transition.flowId,
          type: transition.type,
          includeAll: transition.includeAll,
          isPickup: transition.isPickup ?? false,
          excludedCount: transition.excludedCount,
          includedCount: transition.includedCount,
          createdAt: transition.createdAt,
          updatedAt: transition.updatedAt,
        })),
      providesTags: ['Transitions'],
    }),
    getTransitionById: builder.query<Transition, { transitionId: string }>({
      query: ({ transitionId }) => ({
        url: `/${transitionId}`,
      }),
      transformResponse: (response: TransitionApi) => ({
        id: response.id,
        name: response.name,
        toStatusId: response.toStatusId,
        flowId: response.flowId,
        type: response.type,
        includeAll: response.includeAll,
        isPickup: response.isPickup ?? false,
        excludedCount: response.excludedCount,
        includedCount: response.includedCount,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      providesTags: (_result, _error, { transitionId }) => [
        { type: 'Transitions' as const, id: transitionId },
      ],
    }),
    createTransition: builder.mutation<Transition, NewTransition>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TransitionApi) => ({
        id: response.id,
        name: response.name,
        toStatusId: response.toStatusId,
        flowId: response.flowId,
        type: response.type,
        includeAll: response.includeAll,
        isPickup: response.isPickup ?? false,
        excludedCount: response.excludedCount,
        includedCount: response.includedCount,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      invalidatesTags: ['Transitions'],
    }),
    updateTransition: builder.mutation<Transition, UpdateTransition>({
      query: ({ id, ...body }) => ({
        url: `/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: TransitionApi) => ({
        id: response.id,
        name: response.name,
        toStatusId: response.toStatusId,
        flowId: response.flowId,
        type: response.type,
        includeAll: response.includeAll,
        isPickup: response.isPickup ?? false,
        excludedCount: response.excludedCount,
        includedCount: response.includedCount,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Transitions',
        { type: 'Transitions' as const, id },
      ],
    }),
    deleteTransition: builder.mutation<void, string>({
      query: (transitionId) => ({
        url: `/${transitionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Transitions'],
    }),
  }),
});

export default transitionsService;
export const {
  useGetTransitionsQuery,
  useGetTransitionByIdQuery,
  useCreateTransitionMutation,
  useUpdateTransitionMutation,
  useDeleteTransitionMutation,
} = transitionsService;
